import { message, Spin, Switch } from "antd";
import React from "react";
import { getModulesOrder } from "../apis";
import { MODULE_DEFINITIONS } from "../data/MODULE_DEFINITIONS";

// Module definitions with their display names and IDs

function ModulesPage({ userInfo, modules, setModules }) {
  const [loading, setLoading] = React.useState(true);

  // Fetch modules from API on component mount
  React.useEffect(() => {
    const fetchModules = async () => {
      try {
        setLoading(true);
        const response = await getModulesOrder();
        const userPermissions = userInfo?.permissions || [];

        // Normalize API-provided order (names/keys)
        const modulesFromAPI = response.modules || [];

        // Build a full ordered list: API order first, then any remaining MODULE_DEFINITIONS
        const definitionEntries = Object.entries(MODULE_DEFINITIONS); // [key, def]
        // Exclude Home from definitions for modules page
        const definitionKeys = definitionEntries
          .filter(([, def]) => {
            const label = (def && (def.label || def.name)) || "";
            const key = def && def.key;
            return !(label.toLowerCase() === "home" || key === "/");
          })
          .map(([key]) => key);

        const seen = new Set();
        const orderedKeys = [];

        // 1) Include keys present in API response if they exist in definitions, else include as unknown
        for (const name of modulesFromAPI) {
          if (!seen.has(name)) {
            seen.add(name);
            orderedKeys.push(name);
          }
        }
        // 2) Append any remaining definition keys not present in API order
        for (const key of definitionKeys) {
          if (!seen.has(key)) {
            seen.add(key);
            orderedKeys.push(key);
          }
        }

        // Map ordered keys to module objects with permission logic
        const modulesWithPermissions = orderedKeys.map((name) => {
          const moduleDef = MODULE_DEFINITIONS[name];
          if (!moduleDef) {
            // Unknown module coming from API; compute a safe id and permission check
            const unknownId = name.toLowerCase().replace(/\s+/g, "_");
            const hasPermission =
              userPermissions.includes(unknownId) ||
              userPermissions.includes(name);
            return {
              id: unknownId,
              name,
              enabled: !!hasPermission,
              canReorder: !!hasPermission,
            };
          }

          // New MODULE_DEFINITIONS schema: { key, label, permissions, route }
          const id = (moduleDef.key || name)
            .toString()
            .replace(/^\//, "")
            .toLowerCase()
            .replace(/\s+/g, "_");
          const displayName = moduleDef.label || name;
          // Home is never included here by earlier filtering, but guard anyway
          if (displayName.toLowerCase() === "home" || moduleDef.key === "/") {
            return null;
          }
          const permissionKey = moduleDef.permissions || id;
          const hasPermission = userPermissions.includes(permissionKey);

          return {
            id,
            name: displayName,
            enabled: !!hasPermission,
            canReorder: !!hasPermission,
          };
        });
        // Filter out any nulls (e.g., guarded Home)
        const filteredModules = modulesWithPermissions.filter(Boolean);
        setModules(filteredModules);
      } catch (error) {
        console.error("Error fetching modules:", error);
        message.error("Errore nel caricamento dei moduli");

        // Fallback to default modules if API fails
        const userPermissions = userInfo?.permissions || [];
        const fallbackModules = Object.values(MODULE_DEFINITIONS)
          .filter((module) => {
            const label = (module && (module.label || module.name)) || "";
            const key = module && module.key;
            return !(label.toLowerCase() === "home" || key === "/");
          })
          .map((module) => {
            const id = (module.key || module.label || "")
              .toString()
              .replace(/^\//, "")
              .toLowerCase()
              .replace(/\s+/g, "_");
            const displayName = module.label || id;
            const permissionKey = module.permissions || id;
            const hasPermission = userPermissions.includes(permissionKey);
            return {
              id,
              name: displayName,
              enabled: !!hasPermission,
              canReorder: !!hasPermission,
            };
          });
        setModules(fallbackModules);
      } finally {
        setLoading(false);
      }
    };

    if (userInfo) {
      fetchModules();
    }
  }, [userInfo, setModules]);

  const handleToggle = (id, val) => {
    setModules((prev) =>
      prev.map((m) => (m.id === id ? { ...m, enabled: val } : m))
    );
  };

  const [draggedIndex, setDraggedIndex] = React.useState(null);

  const handleDragStart = (e, index) => {
    const module = modules[index];
    if (!module.canReorder) {
      e.preventDefault();
      return;
    }
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();

    if (draggedIndex === null) return;
    if (draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    setModules((prev) => {
      const fromModule = prev[draggedIndex];
      const toModule = prev[dropIndex];

      // Check if both modules can be reordered
      if (!fromModule.canReorder || !toModule.canReorder) {
        message.warning(
          "Non puoi riordinare moduli per cui non hai i permessi"
        );
        return prev;
      }

      const next = [...prev];
      const [moved] = next.splice(draggedIndex, 1);
      next.splice(dropIndex, 0, moved);
      return next;
    });

    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {modules.map((m, idx) => (
          <li
            key={m.id}
            draggable={m.canReorder}
            onDragStart={(e) => handleDragStart(e, idx)}
            onDragOver={(e) => handleDragOver(e, idx)}
            onDrop={(e) => handleDrop(e, idx)}
            onDragEnd={handleDragEnd}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 12px",
              margin: "8px 0",
              border: "1px solid #f0f0f0",
              borderRadius: 10,
              background: "#fff",
              boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
              cursor: m.canReorder ? "grab" : "default",
              opacity: m.canReorder ? 1 : 0.6,
            }}
          >
            <span
              aria-label="drag"
              title={
                m.canReorder
                  ? "Trascina per riordinare"
                  : "Non puoi riordinare questo modulo"
              }
              style={{
                width: 18,
                textAlign: "center",
                color: m.canReorder ? "#94a3b8" : "#d1d5db",
                cursor: m.canReorder ? "grab" : "not-allowed",
              }}
            >
              ••
            </span>
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                background: m.enabled ? "#f4f7ee" : "#f3f4f6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: m.enabled ? "#3a4d1b" : "#9ca3af",
                fontWeight: 700,
                textTransform: "uppercase",
                fontSize: 12,
              }}
            >
              {m.name.charAt(0)}
            </div>
            <div
              style={{
                flex: 1,
                color: m.enabled ? "#000" : "#9ca3af",
              }}
            >
              {m.name}
            </div>
            <Switch
              checked={m.enabled}
              onChange={(v) => handleToggle(m.id, v)}
              disabled={!m.canReorder}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

export default ModulesPage;
